import type { PaginationProps } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type { ColorToken, PaginationToken } from 'hephaestus/core';
import { useTheme } from 'hephaestus/web';
import { usePaginationTokens } from './pagination.tokens';

type PaginationSxVariant = 'default' | 'tableFooter';

const buildLegacyPaginationSx = (
  extraSx: PaginationProps['sx'] | undefined,
  paginationTokens: PaginationToken,
): SxProps<Theme> => ({
  ...extraSx,
  '& .MuiPaginationItem-page:not(.Mui-selected)': {
    color: paginationTokens.item?.color,
    backgroundColor: paginationTokens.item?.backgroundColor,
    fontSize: paginationTokens.item?.fontSize,
    fontWeight: paginationTokens.item?.fontWeight,
    borderRadius: paginationTokens.item?.borderRadius,
    minWidth: paginationTokens.item?.minWidth,
    height: paginationTokens.item?.height,
    '&:hover': {
      backgroundColor: paginationTokens.item?.hoverBackgroundColor,
    },
  },
  '& .MuiPaginationItem-page.Mui-selected': {
    backgroundColor: paginationTokens.selectedItem?.backgroundColor,
    color: paginationTokens.selectedItem?.color,
    borderColor: paginationTokens.selectedItem?.borderColor,
    borderWidth: paginationTokens.selectedItem?.borderWidth,
    borderStyle: paginationTokens.selectedItem?.borderStyle,
    borderRadius:
      paginationTokens.selectedItem?.borderRadius ||
      paginationTokens.item?.borderRadius,
    fontWeight:
      paginationTokens.selectedItem?.fontWeight ||
      paginationTokens.item?.fontWeight,
    minWidth: paginationTokens.item?.minWidth,
    height: paginationTokens.item?.height,
    fontSize: paginationTokens.item?.fontSize,
    '&:hover': {
      backgroundColor: paginationTokens.selectedItem?.hoverBackgroundColor,
    },
  },
  '& .MuiPaginationItem-previousNext': {
    backgroundColor: paginationTokens.navigation?.backgroundColor,
    color: paginationTokens.navigation?.color,
    borderRadius: paginationTokens.navigation?.borderRadius,
    minWidth: paginationTokens.navigation?.size,
    height: paginationTokens.navigation?.size,
    '& .MuiPaginationItem-icon': {
      fontSize: paginationTokens.navigation?.iconFontSize,
    },
    '&:hover': {
      backgroundColor: paginationTokens.navigation?.hoverBackgroundColor,
    },
  },
  '& .MuiPaginationItem-ellipsis': {
    color: paginationTokens.ellipsis?.color || paginationTokens.item?.color,
  },
});

const buildTableFooterPaginationSx = (
  extraSx: PaginationProps['sx'] | undefined,
  paginationTokens: PaginationToken,
  color: ColorToken,
): SxProps<Theme> => {
  const outline = color.outlineVariant ?? color.outline;
  const surface = color.surface ?? '#fff';
  const muted = color.onSurfaceVariant ?? color.onSurface;
  const primary = color.primary;
  const onPrimary = color.onPrimary;

  return {
    ...extraSx,
    '& .MuiPaginationItem-root': {
      border: `1px solid ${outline}`,
      boxSizing: 'border-box' as const,
    },
    '& .MuiPaginationItem-ellipsis': {
      border: 'none',
      color:
        paginationTokens.ellipsis?.color ??
        paginationTokens.item?.color ??
        muted,
    },
    '& .MuiPaginationItem-page:not(.Mui-selected)': {
      color: paginationTokens.item?.color ?? muted,
      backgroundColor: paginationTokens.item?.backgroundColor ?? surface,
      fontSize: paginationTokens.item?.fontSize ?? '14px',
      fontWeight: paginationTokens.item?.fontWeight ?? 500,
      borderRadius: paginationTokens.item?.borderRadius ?? '6px',
      minWidth: paginationTokens.item?.minWidth ?? '36px',
      height: paginationTokens.item?.height ?? '36px',
      '&:hover': {
        backgroundColor:
          paginationTokens.item?.hoverBackgroundColor ??
          color.surfaceContainerVariant,
      },
    },
    '& .MuiPaginationItem-page.Mui-selected': {
      backgroundColor:
        paginationTokens.selectedItem?.backgroundColor ?? primary,
      color: paginationTokens.selectedItem?.color ?? onPrimary,
      borderColor: paginationTokens.selectedItem?.borderColor ?? primary,
      borderWidth: paginationTokens.selectedItem?.borderWidth ?? '1px',
      borderStyle: paginationTokens.selectedItem?.borderStyle ?? 'solid',
      borderRadius:
        paginationTokens.selectedItem?.borderRadius ??
        paginationTokens.item?.borderRadius ??
        '6px',
      fontWeight:
        paginationTokens.selectedItem?.fontWeight ??
        paginationTokens.item?.fontWeight ??
        600,
      minWidth: paginationTokens.item?.minWidth ?? '36px',
      height: paginationTokens.item?.height ?? '36px',
      fontSize: paginationTokens.item?.fontSize ?? '14px',
      '&:hover': {
        backgroundColor:
          paginationTokens.selectedItem?.hoverBackgroundColor ?? primary,
      },
    },
    '& .MuiPaginationItem-previousNext, & .MuiPaginationItem-firstLast': {
      backgroundColor: paginationTokens.navigation?.backgroundColor ?? surface,
      color: paginationTokens.navigation?.color ?? muted,
      borderRadius: paginationTokens.navigation?.borderRadius ?? '6px',
      minWidth: paginationTokens.navigation?.size ?? '36px',
      height: paginationTokens.navigation?.size ?? '36px',
      '& .MuiPaginationItem-icon': {
        fontSize: paginationTokens.navigation?.iconFontSize ?? '1.25rem',
      },
      '&:hover': {
        backgroundColor:
          paginationTokens.navigation?.hoverBackgroundColor ??
          color.surfaceContainerVariant,
      },
    },
  };
};

export const usePaginationSx = (
  extraSx?: PaginationProps['sx'],
  ovVariant: PaginationSxVariant = 'default',
): SxProps<Theme> => {
  const theme = useTheme();
  const tokens = usePaginationTokens(theme);

  return ovVariant === 'tableFooter'
    ? buildTableFooterPaginationSx(extraSx, tokens, theme.color)
    : buildLegacyPaginationSx(extraSx, tokens);
};
